<template>
    <div class="flute-fingering-wrapper">
        <svg
            ref="svgRef"
            xmlns="http://www.w3.org/2000/svg"
            :width="width"
            :height="height"
            :viewBox="viewBox"
            class="flute-diagram"
        >
            <defs>
                <filter
                    id="highlight-glow"
                    x="-50%"
                    y="-50%"
                    width="250%"
                    height="250%"
                >
                    <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                    <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            <g id="allkeys" :transform="rotationTransform">
                <rect
                    id="background"
                    x="-1.5"
                    y="-1.5"
                    width="496.24"
                    height="178.17"
                    stroke="none"
                    :fill="bgColor"
                />

                <!-- Left hand holes -->
                <circle
                    id="l1"
                    cx="31.5"
                    cy="92.93"
                    r="21"
                    :fill="getHoleFill('l1')"
                    :stroke="getHoleStroke('l1')"
                    :style="getHighlightStyle('l1')"
                    stroke-width="2"
                />
                <circle
                    id="l2"
                    cx="91.5"
                    cy="92.93"
                    r="21"
                    :fill="getHoleFill('l2')"
                    :stroke="getHoleStroke('l2')"
                    :style="getHighlightStyle('l2')"
                    stroke-width="2"
                />
                <circle
                    id="l3"
                    cx="151.5"
                    cy="92.93"
                    r="21"
                    :fill="getHoleFill('l3')"
                    :stroke="getHoleStroke('l3')"
                    :style="getHighlightStyle('l3')"
                    stroke-width="2"
                />

                <!-- Separator -->
                <path
                    id="separator"
                    d="M190.51 112.95 V72.95"
                    fill="none"
                    :stroke="strokeColor"
                    stroke-width="2"
                />

                <!-- Right hand holes -->
                <circle
                    id="r1"
                    cx="231.5"
                    cy="92.93"
                    r="21"
                    :fill="getHoleFill('r1')"
                    :stroke="getHoleStroke('r1')"
                    :style="getHighlightStyle('r1')"
                    stroke-width="2"
                />
                <circle
                    id="r2"
                    cx="291.5"
                    cy="92.93"
                    r="21"
                    :fill="getHoleFill('r2')"
                    :stroke="getHoleStroke('r2')"
                    :style="getHighlightStyle('r2')"
                    stroke-width="2"
                />
                <circle
                    id="r3"
                    cx="351.5"
                    cy="92.93"
                    r="21"
                    :fill="getHoleFill('r3')"
                    :stroke="getHoleStroke('r3')"
                    :style="getHighlightStyle('r3')"
                    stroke-width="2"
                />

                <!-- Thumb keys -->
                <g id="thumbkeys">
                    <path
                        id="thumbb"
                        d="M75.16 130.26C68.27 130.23 62.35 130.89 58.09 133.07C53.84 135.26 51.39 139.20 51.91 144.98C53.55 149.86 56.68 153.45 60.94 156.20C64.71 158.24 69.08 158.77 73.56 158.23C82.49 157.16 92.00 151.96 99.31 145.76C99.35 145.73 99.40 145.70 99.44 145.67C105.87 141.58 105.76 139.40 121.97 135.29C122.45 135.17 122.77 134.71 122.72 134.21C122.66 133.72 122.25 133.34 121.75 133.32C106.62 133.18 88.93 130.32 75.16 130.26Z"
                        :fill="getHoleFill('thumbb')"
                        :stroke="getHoleStroke('thumbb')"
                        :style="getHighlightStyle('thumbb')"
                        stroke-width="2"
                    />
                    <path
                        id="thumbbflat"
                        d="M36.22 122.82C29.92 122.94 23.10 124.95 19.21 130.23C15.40 135.36 14.55 142.07 14.96 148.29C15.59 155.20 20.00 162.15 26.87 164.21C30.67 165.46 34.96 164.11 37.64 161.21C42.51 156.11 43.90 148.63 43.21 141.81C43.20 139.73 42.39 137.49 43.62 135.59C45.89 131.50 49.92 128.80 53.84 126.48C54.91 124.24 51.64 124.92 50.39 124.34C45.74 123.37 40.99 122.69 36.22 122.82Z"
                        :fill="getHoleFill('thumbbflat')"
                        :stroke="getHoleStroke('thumbbflat')"
                        :style="getHighlightStyle('thumbbflat')"
                        stroke-width="2"
                    />
                </g>

                <!-- G-sharp key -->
                <g id="gsharpkeys">
                    <path
                        id="gsharp"
                        d="M152.76 25.54C149.94 25.71 147.28 26.32 144.98 27.29C140.38 29.24 137.08 32.83 137.26 37.38C137.44 41.73 140.03 46.25 146.01 49.45C151.84 52.56 160.97 54.47 174.51 54.26V63.95C174.51 64.50 174.96 64.95 175.51 64.95H180.83C181.34 64.95 181.78 64.56 181.83 64.04C183.15 52.43 181.08 43.77 177.14 37.63C173.20 31.50 167.41 27.95 161.51 26.42C158.56 25.65 155.58 25.37 152.76 25.54Z"
                        :fill="getHoleFill('gsharp')"
                        :stroke="getHoleStroke('gsharp')"
                        :style="getHighlightStyle('gsharp')"
                        stroke-width="2"
                    />
                </g>

                <!-- Trill keys -->
                <g id="trillkeys">
                    <ellipse
                        id="trill1"
                        cx="261.69"
                        cy="125.48"
                        rx="6.56"
                        ry="7.53"
                        :fill="getHoleFill('trill1')"
                        :stroke="getHoleStroke('trill1')"
                        :style="getHighlightStyle('trill1')"
                        stroke-width="2"
                    />
                    <ellipse
                        id="trill2"
                        cx="321.31"
                        cy="125.48"
                        rx="6.56"
                        ry="7.53"
                        :fill="getHoleFill('trill2')"
                        :stroke="getHoleStroke('trill2')"
                        :style="getHighlightStyle('trill2')"
                        stroke-width="2"
                    />
                </g>

                <!-- Spatula keys -->
                <g id="spatulas">
                    <path
                        id="csharptrill"
                        d="M195.01 116.95C192.89 116.95 191.05 118.26 189.79 120.13C188.54 122.01 187.79 124.51 187.79 127.26C187.79 127.99 187.98 130.19 188.04 130.88C188.03 131.31 187.66 132.20 186.79 133.17C185.95 134.11 184.66 135.13 183.11 136.01L182.79 136.23C182.68 136.31 182.61 136.38 182.61 136.38C182.48 136.57 182.42 136.76 182.42 136.76C182.39 137.07 182.51 137.48 182.51 137.48C182.51 137.48 182.76 137.79 182.76 137.79C183.04 137.95 183.23 137.98 183.61 137.98C190.02 139.46 194.83 139.33 198.17 137.42C201.57 135.47 203.14 131.69 203.14 126.79C203.14 121.18 199.16 116.95 195.01 116.95Z"
                        :fill="getHoleFill('csharptrill')"
                        :stroke="getHoleStroke('csharptrill')"
                        :style="getHighlightStyle('csharptrill')"
                        stroke-width="2"
                    />
                    <path
                        id="spatulabflat"
                        d="M216.39 123.10C214.27 123.10 212.42 124.38 211.17 126.26C209.91 128.14 209.17 130.64 209.17 133.38C209.17 134.11 209.35 136.32 209.42 137.01C209.41 137.43 209.03 138.36 208.17 139.32C207.32 140.27 206.05 141.28 204.48 142.17L204.17 142.38C204.17 142.38 203.98 142.51 203.98 142.51C203.89 142.70 203.79 142.92 203.79 142.92C203.79 143.20 203.89 143.60 203.89 143.60C204.14 143.92 204.42 144.07 204.61 144.13C211.44 145.56 216.22 145.45 219.54 143.54C222.94 141.59 224.51 137.85 224.51 132.95C224.51 127.33 220.54 123.10 216.39 123.10Z"
                        :fill="getHoleFill('spatulabflat')"
                        :stroke="getHoleStroke('spatulabflat')"
                        :style="getHighlightStyle('spatulabflat')"
                        stroke-width="2"
                    />
                </g>

                <!-- Right pinky keys -->
                <g id="rpinky">
                    <!-- D-sharp key -->
                    <path
                        id="dsharp"
                        d="M411.59 65.97C411.08 66.02 410.69 66.45 410.69 66.97V87.25C410.69 87.25 416.38 87.25 416.38 87.25C416.38 92.73 416.84 100.62 415.66 107.03C415.06 110.24 414.06 113.09 412.22 115.13C410.38 117.16 407.63 118.25 404.16 117.75C396.47 116.65 390.73 110.69 389.34 103.13C387.96 95.57 390.93 86.44 400.19 78.88C404.07 75.71 406.80 73.89 408.44 72.32C410.08 70.74 410.69 69.54 410.69 66.97C410.69 66.45 411.08 66.02 411.59 65.97H415.38C415.93 65.97 416.38 66.42 416.38 66.97V87.25C416.38 87.25 410.69 87.25 410.69 87.25Z"
                        :fill="getHoleFill('dsharp')"
                        :stroke="getHoleStroke('dsharp')"
                        :style="getHighlightStyle('dsharp')"
                        stroke-width="2"
                    />

                    <!-- Low notes -->
                    <g id="lownotes">
                        <path
                            id="lowdflat"
                            d="M437.72 97.77C435.08 97.79 432.35 97.91 429.87 98.39C427.40 98.87 425.16 99.69 423.56 101.20C421.96 102.72 421.15 104.98 421.53 107.80C422.37 113.98 426.77 118.68 432.47 119.83C438.17 120.98 445.08 118.54 450.87 111.11C453.28 108.02 454.81 105.85 456.16 104.55C457.50 103.24 458.54 102.77 460.44 102.77C460.99 102.77 461.44 102.32 461.44 101.77V98.83C461.44 98.28 460.99 97.83 460.44 97.83H445.00C442.92 97.83 440.36 97.74 437.72 97.77Z"
                            :fill="getHoleFill('lowdflat')"
                            :stroke="getHoleStroke('lowdflat')"
                            :style="getHighlightStyle('lowdflat')"
                            stroke-width="2"
                        />
                        <rect
                            id="lowc"
                            x="421.59"
                            y="82.67"
                            width="33.81"
                            height="10.96"
                            rx="5.48"
                            :fill="getHoleFill('lowc')"
                            :stroke="getHoleStroke('lowc')"
                            :style="getHighlightStyle('lowc')"
                            stroke-width="2"
                        />
                        <rect
                            id="lowb"
                            x="421.63"
                            y="67.55"
                            width="33.81"
                            height="10.98"
                            rx="5.49"
                            :fill="getHoleFill('lowb')"
                            :stroke="getHoleStroke('lowb')"
                            :style="getHighlightStyle('lowb')"
                            stroke-width="2"
                        />
                    </g>

                    <!-- Gizmo key -->
                    <ellipse
                        id="gizmo"
                        cx="472.5"
                        cy="87"
                        rx="10"
                        ry="10"
                        :fill="getHoleFill('gizmo')"
                        :stroke="getHoleStroke('gizmo')"
                        :style="getHighlightStyle('gizmo')"
                        stroke-width="2"
                    />
                </g>
            </g>
        </svg>

        <div v-if="note" class="note-label">
            <strong>{{ note }}</strong>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

interface FingeringState {
    // Main holes (0 = open, 1 = closed, 0.5 = half-closed)
    l1?: number;
    l2?: number;
    l3?: number;
    r1?: number;
    r2?: number;
    r3?: number;

    // Thumb keys
    thumbb?: number;
    thumbbflat?: number;

    // Special keys
    gsharp?: number;
    trill1?: number;
    trill2?: number;
    csharptrill?: number;
    spatulabflat?: number;
    dsharp?: number;
    lowb?: number;
    lowc?: number;
    lowdflat?: number;
    gizmo?: number;
}

interface Props {
    note?: string;
    fingering?: FingeringState;
    width?: number;
    height?: number;
    viewBox?: string;
    orientation?: "horizontal" | "vertical";
    bgColor?: string;
    fillColor?: string;
    strokeColor?: string;
    highlightColor?: string;
    highlights?: string[];
}

const props = withDefaults(defineProps<Props>(), {
    width: 500,
    height: 200,
    viewBox: "0 0 493.24 197.18",
    orientation: "horizontal",
    bgColor: "transparent",
    fillColor: "black",
    strokeColor: "black",
    highlightColor: "#FFD700",
    highlights: () => [],
});

const svgRef = ref<SVGSVGElement | null>(null);

const rotationTransform = computed(() => {
    if (props.orientation === "vertical") {
        // Rotate 90 degrees clockwise around the center of the viewBox
        const centerX = 493.24 / 2;
        const centerY = 197.18 / 2;
        return `rotate(90 ${centerX} ${centerY})`;
    }
    return "";
});

const fingeringState = computed(() => props.fingering || {});

function getHoleFill(keyId: string): string {
    const state = fingeringState.value[keyId as keyof FingeringState];

    if (state === undefined) {
        return "white";
    }

    if (state === 1) return props.fillColor;
    if (state >= 0.5) return "#999";
    return "white";
}

function getHoleStroke(keyId: string): string {
    return props.strokeColor;
}

function getHighlightStyle(keyId: string): Record<string, string> {
    const isHighlighted = props.highlights?.includes(keyId);
    if (isHighlighted) {
        return {
            filter: "url(#highlight-glow)",
            stroke: props.highlightColor,
        };
    }
    return {};
}

function getKeyDisplay(keyId: string): boolean {
    const state = fingeringState.value[keyId as keyof FingeringState];
    return state !== undefined && state > 0;
}
</script>

<style scoped>
.flute-fingering-wrapper {
    display: inline-block;
    text-align: center;
}

.flute-diagram {
    display: block;
    margin: 0 auto;
    /*border: 1px solid #ddd;
    border-radius: 4px;*/
    /*background: white;*/
}

.note-label {
    margin-top: 12px;
    font-size: 14px;
    color: #333;
}
</style>
